/**
 * KYCVerification.jsx
 *
 * Premium Dark Mode KYC Wizard:
 *   1. Consent & Type Selection (Individual vs. Company)
 *   2. Path Selector:
 *      - Path A: Automatic Sandboxed API Verification (Instant)
 *      - Path B: Manual Document Upload Verification (Admin Approval)
 *
 * We group number entry and file upload together in both flows:
 *   - Enter number -> (API Verify if Auto) -> Upload document file
 * Keep all government database APIs fully active and available.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Chip,
  Divider, FormControlLabel, Paper, TextField, Typography,
  Card, CardContent, ToggleButton, ToggleButtonGroup,
  Stepper, Step, StepLabel, StepContent, useTheme
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import SecurityIcon from '@mui/icons-material/Security';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { apiClient } from '../utils/auth';

const ACCEPT = '.pdf,.jpg,.jpeg,.png';
const MAX_SIZE = 5 * 1024 * 1024; // 5 MB

export default function KYCVerification() {
  const navigate = useNavigate();
  const theme = useTheme();

  // Onboarding parameters
  const [isCompany, setIsCompany] = useState(false);
  const [consent, setConsent] = useState(false);
  
  // Verification Mode: 'auto' | 'manual'
  const [verifyMode, setVerifyMode] = useState('auto');
  
  // API matching statuses from backend
  const [statuses, setStatuses] = useState({});
  const [allMatched, setAllMatched] = useState(false);
  
  // Input fields
  const [pan, setPan] = useState('');
  const [panName, setPanName] = useState('');
  const [panDob, setPanDob] = useState('');
  const [aadhaarNum, setAadhaarNum] = useState('');
  const [aadhaarOtp, setAadhaarOtp] = useState('');
  const [gstin, setGstin] = useState('');
  const [cin, setCin] = useState('');
  const [companyName, setCompanyName] = useState('');

  // File Upload states
  const [files, setFiles] = useState({});

  // Loading & UI states
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');

  // Fetch current KYC statuses from backend
  const fetchStatuses = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/api/v1/kyc/verify/status/');
      setStatuses(data.verifications || {});
      setAllMatched(data.all_matched && Object.keys(data.verifications || {}).length > 0);
      
      const vers = data.verifications || {};
      if (data.pan_number) setPan(data.pan_number);
      if (data.gst_number) setGstin(data.gst_number);
      if (data.cin_number) setCin(data.cin_number);
      if (data.aadhaar_reference) setAadhaarNum(data.aadhaar_reference);
    } catch (err) {
      console.error('Failed to fetch verification status', err);
    }
  }, []);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  // Handle File Input
  const handleFileChange = (key, e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > MAX_SIZE) {
      setError(`${key} document exceeds the maximum 5 MB limit.`);
      return;
    }
    setError('');
    setFiles(prev => ({ ...prev, [key]: f }));
  };

  // ──────────────────────────────────────────────────────────────────────────
  // FLOW 1: AUTOMATIC VERIFICATION API HANDLERS
  // ──────────────────────────────────────────────────────────────────────────
  
  const handleVerifyPAN = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await apiClient.post('/api/v1/kyc/verify/pan/', {
        pan,
        name: panName,
        date_of_birth: panDob
      });
      await fetchStatuses();
      if (data.status !== 'MATCHED') {
        setError('PAN verification failed. Please check your details and try again.');
      } else {
        // Proceed to file attachment step
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'PAN Verification failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendAadhaarOTP = async () => {
    setLoading(true); setError('');
    try {
      await apiClient.post('/api/v1/kyc/verify/aadhaar/otp/', { aadhaar_number: aadhaarNum });
      setOtpSent(true);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Could not send Aadhaar OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAadhaarOTP = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await apiClient.post('/api/v1/kyc/verify/aadhaar/confirm/', { otp: aadhaarOtp });
      await fetchStatuses();
      if (data.status !== 'MATCHED') {
        setError('Aadhaar OTP verification failed. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Aadhaar confirmation failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyGST = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await apiClient.post('/api/v1/kyc/verify/gstin/', { gstin });
      await fetchStatuses();
      if (data.status !== 'MATCHED') {
        setError('GST verification failed. Please verify your GSTIN.');
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'GST Verification failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMCA = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await apiClient.post('/api/v1/kyc/verify/mca/', { cin, company_name: companyName });
      await fetchStatuses();
      if (data.status !== 'MATCHED') {
        setError('MCA Company verification failed.');
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'MCA Verification failed.');
    } finally {
      setLoading(false);
    }
  };

  // Submit Feedback / Support ticket on Automated Failure
  const handleFeedbackSubmit = async (selectedType) => {
    setLoading(true); setError('');
    try {
      await apiClient.post('/api/v1/kyc/verify/feedback/', {
        verification_type: selectedType,
        feedback: feedbackText
      });
      setFeedbackSubmitted(true);
      setFeedbackText('');
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to submit issue report.');
    } finally {
      setLoading(false);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // FINAL SUBMISSION (BOTH PATHS)
  // ──────────────────────────────────────────────────────────────────────────
  
  const handleFinalSubmit = async () => {
    setError('');
    
    // Check mandatory uploads
    const requiredDocs = isCompany ? ['PAN', 'AADHAAR_REF', 'CIN'] : ['PAN', 'AADHAAR_REF'];
    const missing = requiredDocs.filter(d => !files[d]);
    
    if (missing.length > 0) {
      setError(`Please upload files for: ${missing.join(', ')}`);
      return;
    }

    if (!pan.trim()) { setError('PAN Number is required.'); return; }
    if (!aadhaarNum.trim()) { setError('Aadhaar Number/Reference is required.'); return; }
    if (isCompany && !cin.trim()) { setError('CIN/LLPIN is required.'); return; }

    setLoading(true);
    try {
      const uploadSlots = isCompany ? ['PAN', 'AADHAAR_REF', 'CIN'] : ['PAN', 'AADHAAR_REF'];
      if (isCompany && files['GST']) {
        uploadSlots.push('GST');
      }

      for (const docKey of uploadSlots) {
        const fd = new FormData();
        fd.append('doc_type', docKey);
        fd.append('file', files[docKey]);
        fd.append('pan_number', pan.trim().toUpperCase());
        fd.append('gst_number', gstin.trim().toUpperCase());
        
        let aadh = aadhaarNum.trim();
        // If not already in masked format and user inputted last 4 digits or raw digits
        if (!aadh.includes('X') && !aadh.includes('*')) {
          const digits = aadh.replace(/\D/g, '');
          aadh = digits.length >= 4 ? `XXXX-XXXX-${digits.slice(-4)}` : `XXXX-XXXX-1234`;
        }
        fd.append('aadhaar_reference', aadh);
        fd.append('is_company', String(isCompany));
        if (isCompany) fd.append('cin_number', cin.trim().toUpperCase());
        
        await apiClient.post('/api/v1/kyc/upload/', fd);
      }

      setSuccessMsg(
        verifyMode === 'auto'
          ? 'KYC Verification fully completed! Your account is now ACTIVE.'
          : 'KYC documents and identity numbers submitted for manual verification. An administrator will review your files shortly.'
      );
    } catch (err) {
      setError(err.response?.data?.error?.message || 'KYC Upload submission failed. Please check inputs.');
    } finally {
      setLoading(false);
    }
  };

  if (successMsg) {
    return (
      <Box sx={{ maxWidth: 660, mx: 'auto', mt: 8, p: 3 }}>
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderColor: 'success.main', bgcolor: 'rgba(16, 185, 129, 0.04)' }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h5" gutterBottom fontWeight={700}>KYC Submission Received</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {successMsg}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/')}>
            Return to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 680, mx: 'auto', mt: 4, p: 3 }}>
      <Button
        variant="outlined"
        size="small"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{
          mb: 3,
          borderColor: 'rgba(99, 102, 241, 0.4)',
          color: '#818cf8',
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          '&:hover': {
            borderColor: '#6366f1',
            backgroundColor: 'rgba(99, 102, 241, 0.08)',
          }
        }}
      >
        Back
      </Button>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <SecurityIcon color="primary" sx={{ fontSize: 36 }} />
        <Typography variant="h4" fontWeight={700} sx={{ letterSpacing: '-0.02em' }}>KYC Verification</Typography>
      </Box>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Complete your KYC onboarding securely. Select either automated verification or direct manual review paths.
      </Typography>

      {/* STEP 1: Consent & Selection */}
      {!consent ? (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>Consent to Verification</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
            To comply with anti-money laundering and corporate identity guidelines, CyberExchange verifies standard identity parameters. 
            All details are immediately masked and encrypted in our database.
          </Typography>
          
          <Box component="ul" sx={{ pl: 2.5, mb: 3, color: 'text.secondary', fontSize: '0.875rem' }}>
            <li>PAN matches Name & DOB against Tax authority database</li>
            <li>Aadhaar validation verified securely via official OTP</li>
            <li>Company details matched against MCA / CIN government registry (if selected)</li>
          </Box>

          <FormControlLabel
            control={<Checkbox checked={isCompany} onChange={e => setIsCompany(e.target.checked)} />}
            label={<Typography variant="body2">I represent a company (requires CIN / LLPIN verification)</Typography>}
            sx={{ mb: 1.5, display: 'flex', alignItems: 'center' }}
          />

          <FormControlLabel
            control={<Checkbox checked={consent} onChange={e => setConsent(e.target.checked)} />}
            label={<Typography variant="body2">I consent to the matching of my details with official government records for KYC.</Typography>}
            sx={{ display: 'flex', alignItems: 'center' }}
          />

          <Button variant="contained" fullWidth sx={{ mt: 3, py: 1.2 }} disabled={!consent} onClick={() => setConsent(true)}>
            Proceed to Verification
          </Button>
        </Paper>
      ) : (
        <>
          {/* Path Toggle: Auto vs Manual */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
            <Typography variant="subtitle2" gutterBottom fontWeight={600}>Choose Verification Mode:</Typography>
            <ToggleButtonGroup
              value={verifyMode}
              exclusive
              onChange={(e, next) => { if (next) setVerifyMode(next); }}
              fullWidth
              size="small"
              sx={{ mt: 1 }}
            >
              <ToggleButton value="auto" aria-label="automatic-mode">
                <FlashOnIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                Automatic API (Instant)
              </ToggleButton>
              <ToggleButton value="manual" aria-label="manual-mode">
                <AssignmentIndIcon fontSize="small" sx={{ mr: 1, color: 'warning.main' }} />
                Manual Upload (Admin Review)
              </ToggleButton>
            </ToggleButtonGroup>
          </Paper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* ──────────────────────────────────────────────────────────────────
              PATH A: AUTOMATIC VERIFICATION WIZARD
              ────────────────────────────────────────────────────────────────── */}
          {verifyMode === 'auto' && (
            <Stepper activeStep={activeStep} orientation="vertical">
              
              {/* Auto Step 1: PAN */}
              <Step>
                <StepLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography fontWeight={600}>Verify PAN Details</Typography>
                    {statuses['PAN']?.status === 'MATCHED' && <Chip label="Verified" size="small" color="success" />}
                  </Box>
                </StepLabel>
                <StepContent>
                  <Card variant="outlined" sx={{ mb: 2, mt: 1 }}>
                    <CardContent sx={{ p: 2 }}>
                      {statuses['PAN']?.status === 'MATCHED' ? (
                        <Box>
                          <Alert severity="success" sx={{ mb: 2 }}>✓ PAN API Verification passed.</Alert>
                          <Typography variant="body2" fontWeight={600} gutterBottom>Attach PAN Document Copy *</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">Upload clear PDF, JPG or PNG (max 5MB)</Typography>
                              {files['PAN'] && <Typography variant="caption" display="block" color="success.main">✓ {files['PAN'].name}</Typography>}
                            </Box>
                            <Button variant={files['PAN'] ? 'outlined' : 'contained'} component="label" size="small" startIcon={<UploadFileIcon />}>
                              {files['PAN'] ? 'Change Copy' : 'Attach File'}
                              <input type="file" hidden accept={ACCEPT} onChange={e => handleFileChange('PAN', e)} />
                            </Button>
                          </Box>
                          <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                            <Button variant="contained" size="small" disabled={!files['PAN']} onClick={() => setActiveStep(1)}>
                              Continue
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <Box>
                          <TextField label="PAN Number *" value={pan} onChange={e => setPan(e.target.value.toUpperCase())} fullWidth size="small" margin="dense" inputProps={{ maxLength: 10 }} helperText="Format: AAAAA9999A" />
                          <TextField label="Full Name *" value={panName} onChange={e => setPanName(e.target.value)} fullWidth size="small" margin="dense" />
                          <TextField label="Date of Birth *" value={panDob} onChange={e => setPanDob(e.target.value)} fullWidth size="small" margin="dense" placeholder="DD/MM/YYYY" helperText="Format: DD/MM/YYYY" />
                          <Button variant="contained" size="small" sx={{ mt: 2 }} disabled={loading || !pan || !panName || !panDob} onClick={handleVerifyPAN}>
                            {loading ? <CircularProgress size={16} /> : 'Verify PAN'}
                          </Button>
                          {statuses['PAN']?.status === 'FAILED' && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="caption" color="error.main" display="block" gutterBottom>Verification failed. Issue reporting:</Typography>
                              <TextField size="small" fullWidth placeholder="Let admin know why it failed..." value={feedbackText} onChange={e => setFeedbackText(e.target.value)} />
                              <Button size="small" color="warning" onClick={() => handleFeedbackSubmit('PAN')} sx={{ mt: 1 }} disabled={loading || !feedbackText}>
                                Submit Issue & Continue Manually
                              </Button>
                            </Box>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </StepContent>
              </Step>

              {/* Auto Step 2: Aadhaar */}
              <Step>
                <StepLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography fontWeight={600}>Verify Aadhaar Number</Typography>
                    {statuses['AADHAAR']?.status === 'MATCHED' && <Chip label="Verified" size="small" color="success" />}
                  </Box>
                </StepLabel>
                <StepContent>
                  <Card variant="outlined" sx={{ mb: 2, mt: 1 }}>
                    <CardContent sx={{ p: 2 }}>
                      {statuses['AADHAAR']?.status === 'MATCHED' ? (
                        <Box>
                          <Alert severity="success" sx={{ mb: 2 }}>✓ Aadhaar Verification passed via secure OTP.</Alert>
                          <Typography variant="body2" fontWeight={600} gutterBottom>Attach Masked Aadhaar Copy *</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">PDF, JPG or PNG (last 4 digits visible, mask rest)</Typography>
                              {files['AADHAAR_REF'] && <Typography variant="caption" display="block" color="success.main">✓ {files['AADHAAR_REF'].name}</Typography>}
                            </Box>
                            <Button variant={files['AADHAAR_REF'] ? 'outlined' : 'contained'} component="label" size="small" startIcon={<UploadFileIcon />}>
                              {files['AADHAAR_REF'] ? 'Change Copy' : 'Attach File'}
                              <input type="file" hidden accept={ACCEPT} onChange={e => handleFileChange('AADHAAR_REF', e)} />
                            </Button>
                          </Box>
                          <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                            <Button variant="contained" size="small" disabled={!files['AADHAAR_REF']} onClick={() => setActiveStep(isCompany ? 2 : 1)}>
                              {isCompany ? 'Continue' : 'Submit KYC'}
                            </Button>
                            <Button size="small" onClick={() => setActiveStep(0)}>Back</Button>
                          </Box>
                        </Box>
                      ) : (
                        <Box>
                          {!otpSent ? (
                            <Box>
                              <TextField label="Aadhaar Number *" value={aadhaarNum} onChange={e => setAadhaarNum(e.target.value.replace(/\D/g, ''))} fullWidth size="small" margin="dense" inputProps={{ maxLength: 12 }} />
                              <Button variant="contained" size="small" sx={{ mt: 2 }} disabled={loading || aadhaarNum.length !== 12} onClick={handleSendAadhaarOTP}>
                                {loading ? <CircularProgress size={16} /> : 'Send OTP'}
                              </Button>
                            </Box>
                          ) : (
                            <Box>
                              <TextField label="Enter OTP *" value={aadhaarOtp} onChange={e => setAadhaarOtp(e.target.value.replace(/\D/g, ''))} fullWidth size="small" margin="dense" inputProps={{ maxLength: 6 }} />
                              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                <Button variant="contained" size="small" disabled={loading || aadhaarOtp.length < 4} onClick={handleVerifyAadhaarOTP}>
                                  Verify OTP
                                </Button>
                                <Button size="small" variant="outlined" onClick={() => setOtpSent(false)}>Back</Button>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </StepContent>
              </Step>

              {/* Auto Step 3 (GST - Company Only) */}
              {isCompany && (
                <Step>
                  <StepLabel>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontWeight={600}>Verify Company GSTIN (Optional)</Typography>
                      {statuses['GST']?.status === 'MATCHED' && <Chip label="Verified" size="small" color="success" />}
                    </Box>
                  </StepLabel>
                  <StepContent>
                    <Card variant="outlined" sx={{ mb: 2, mt: 1 }}>
                      <CardContent sx={{ p: 2 }}>
                        {statuses['GST']?.status === 'MATCHED' ? (
                          <Box>
                            <Alert severity="success" sx={{ mb: 2 }}>✓ GST Identification Number validated.</Alert>
                            <Typography variant="body2" fontWeight={600} gutterBottom>Attach GST Certificate copy</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                              <Box>
                                <Typography variant="caption" color="text.secondary">Optional attachment</Typography>
                                {files['GST'] && <Typography variant="caption" display="block" color="success.main">✓ {files['GST'].name}</Typography>}
                              </Box>
                              <Button variant={files['GST'] ? 'outlined' : 'contained'} component="label" size="small" startIcon={<UploadFileIcon />}>
                                {files['GST'] ? 'Change File' : 'Attach File'}
                                <input type="file" hidden accept={ACCEPT} onChange={e => handleFileChange('GST', e)} />
                              </Button>
                            </Box>
                            <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                              <Button variant="contained" size="small" onClick={() => setActiveStep(3)}>
                                Continue
                              </Button>
                              <Button size="small" onClick={() => setActiveStep(1)}>Back</Button>
                            </Box>
                          </Box>
                        ) : (
                          <Box>
                            <TextField label="GSTIN (Optional)" value={gstin} onChange={e => setGstin(e.target.value.toUpperCase())} fullWidth size="small" margin="dense" inputProps={{ maxLength: 15 }} />
                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                              <Button variant="contained" size="small" disabled={loading || !gstin} onClick={handleVerifyGST}>
                                Verify GSTIN
                              </Button>
                              <Button size="small" variant="outlined" onClick={() => setActiveStep(3)}>Skip Optional GST</Button>
                            </Box>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </StepContent>
                </Step>
              )}

              {/* Auto Step 4 (CIN - Company Only) */}
              {isCompany && (
                <Step>
                  <StepLabel>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontWeight={600}>Verify Company Registration (MCA / CIN)</Typography>
                      {statuses['MCA']?.status === 'MATCHED' && <Chip label="Verified" size="small" color="success" />}
                    </Box>
                  </StepLabel>
                  <StepContent>
                    <Card variant="outlined" sx={{ mb: 2, mt: 1 }}>
                      <CardContent sx={{ p: 2 }}>
                        {statuses['MCA']?.status === 'MATCHED' ? (
                          <Box>
                            <Alert severity="success" sx={{ mb: 2 }}>✓ Company Ministry of Corporate Affairs (CIN) validated.</Alert>
                            <Typography variant="body2" fontWeight={600} gutterBottom>Attach Incorporation / CIN Document *</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                              <Box>
                                <Typography variant="caption" color="text.secondary">Mandatory company certificate</Typography>
                                {files['CIN'] && <Typography variant="caption" display="block" color="success.main">✓ {files['CIN'].name}</Typography>}
                              </Box>
                              <Button variant={files['CIN'] ? 'outlined' : 'contained'} component="label" size="small" startIcon={<UploadFileIcon />}>
                                {files['CIN'] ? 'Change File' : 'Attach File'}
                                <input type="file" hidden accept={ACCEPT} onChange={e => handleFileChange('CIN', e)} />
                              </Button>
                            </Box>
                            <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                              <Button variant="contained" size="small" disabled={!files['CIN']} onClick={handleFinalSubmit}>
                                Submit KYC
                              </Button>
                              <Button size="small" onClick={() => setActiveStep(2)}>Back</Button>
                            </Box>
                          </Box>
                        ) : (
                          <Box>
                            <TextField label="Company CIN / LLPIN *" value={cin} onChange={e => setCin(e.target.value.toUpperCase())} fullWidth size="small" margin="dense" />
                            <TextField label="Company Name *" value={companyName} onChange={e => setCompanyName(e.target.value)} fullWidth size="small" margin="dense" />
                            <Button variant="contained" size="small" sx={{ mt: 2 }} disabled={loading || !cin || !companyName} onClick={handleVerifyMCA}>
                              Verify CIN
                            </Button>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </StepContent>
                </Step>
              )}

            </Stepper>
          )}

          {/* ──────────────────────────────────────────────────────────────────
              PATH B: MANUAL UPLOAD VERIFICATION
              ────────────────────────────────────────────────────────────────── */}
          {verifyMode === 'manual' && (
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Manual Review Application</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Input your document numbers below and attach copy scans. An administrator will verify them.
              </Typography>

              {/* Step 1: PAN Card (Manual) */}
              <Card variant="outlined" sx={{ mb: 2.5 }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} color="primary.main" gutterBottom>1. PAN Card Details</Typography>
                  <TextField label="PAN Number *" value={pan} onChange={e => setPan(e.target.value.toUpperCase())} fullWidth size="small" margin="dense" inputProps={{ maxLength: 10 }} placeholder="AAAAA9999A" sx={{ mb: 2 }} />
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Attach clear scan copy of PAN card *</Typography>
                      {files['PAN'] && <Typography variant="caption" display="block" color="success.main">✓ {files['PAN'].name}</Typography>}
                    </Box>
                    <Button variant={files['PAN'] ? 'outlined' : 'contained'} component="label" size="small" startIcon={<UploadFileIcon />}>
                      {files['PAN'] ? 'Change' : 'Attach'}
                      <input type="file" hidden accept={ACCEPT} onChange={e => handleFileChange('PAN', e)} />
                    </Button>
                  </Box>
                </CardContent>
              </Card>

              {/* Step 2: Aadhaar Card (Manual) */}
              <Card variant="outlined" sx={{ mb: 2.5 }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} color="primary.main" gutterBottom>2. Aadhaar Reference</Typography>
                  <TextField label="Aadhaar Number (12 Digits) *" value={aadhaarNum} onChange={e => setAadhaarNum(e.target.value.replace(/\D/g, ''))} fullWidth size="small" margin="dense" inputProps={{ maxLength: 12 }} placeholder="12-digit Aadhaar number" sx={{ mb: 2 }} />
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Attach Aadhaar copy (mask first 8 digits) *</Typography>
                      {files['AADHAAR_REF'] && <Typography variant="caption" display="block" color="success.main">✓ {files['AADHAAR_REF'].name}</Typography>}
                    </Box>
                    <Button variant={files['AADHAAR_REF'] ? 'outlined' : 'contained'} component="label" size="small" startIcon={<UploadFileIcon />}>
                      {files['AADHAAR_REF'] ? 'Change' : 'Attach'}
                      <input type="file" hidden accept={ACCEPT} onChange={e => handleFileChange('AADHAAR_REF', e)} />
                    </Button>
                  </Box>
                </CardContent>
              </Card>

              {/* Step 3 (GST - Company Only - Manual) */}
              {isCompany && (
                <Card variant="outlined" sx={{ mb: 2.5 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} color="primary.main" gutterBottom>3. GST Certificate (Optional)</Typography>
                    <TextField label="GSTIN" value={gstin} onChange={e => setGstin(e.target.value.toUpperCase())} fullWidth size="small" margin="dense" inputProps={{ maxLength: 15 }} placeholder="15-character GSTIN" sx={{ mb: 2 }} />
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Attach GST registration copy</Typography>
                        {files['GST'] && <Typography variant="caption" display="block" color="success.main">✓ {files['GST'].name}</Typography>}
                      </Box>
                      <Button variant={files['GST'] ? 'outlined' : 'contained'} component="label" size="small" startIcon={<UploadFileIcon />}>
                        {files['GST'] ? 'Change' : 'Attach'}
                        <input type="file" hidden accept={ACCEPT} onChange={e => handleFileChange('GST', e)} />
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Step 4 (CIN - Company Only - Manual) */}
              {isCompany && (
                <Card variant="outlined" sx={{ mb: 2.5 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} color="primary.main" gutterBottom>4. Company CIN / MCA Document</Typography>
                    <TextField label="CIN / LLPIN *" value={cin} onChange={e => setCin(e.target.value.toUpperCase())} fullWidth size="small" margin="dense" placeholder="Company Registration CIN" sx={{ mb: 2 }} />
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Attach certificate of incorporation *</Typography>
                        {files['CIN'] && <Typography variant="caption" display="block" color="success.main">✓ {files['CIN'].name}</Typography>}
                      </Box>
                      <Button variant={files['CIN'] ? 'outlined' : 'contained'} component="label" size="small" startIcon={<UploadFileIcon />}>
                        {files['CIN'] ? 'Change' : 'Attach'}
                        <input type="file" hidden accept={ACCEPT} onChange={e => handleFileChange('CIN', e)} />
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              )}

              <Button variant="contained" color="warning" fullWidth size="large" sx={{ mt: 3 }} disabled={loading} onClick={handleFinalSubmit}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit KYC for Manual Review'}
              </Button>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
}
